import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Users, CheckCircle, BookOpen, Award, Star, ChevronDown, ChevronUp, Upload, Trash2, ExternalLink, Loader, FileText, X } from 'lucide-react';
import { sponsorshipsAPI, projectsAPI, publicAPI } from '../lib/api';
import { EmptyState, Modal, LoadingPage, PageHeader } from '../components/shared';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  pledged:   { color: 'var(--gold)',       label: 'Pledged' },
  confirmed: { color: 'var(--teal)',       label: 'Confirmed' },
  active:    { color: 'var(--success)',    label: 'Active' },
  completed: { color: 'var(--text-muted)', label: 'Completed' },
  cancelled: { color: 'var(--error)',      label: 'Cancelled' },
};

const MILESTONE_STATUS = {
  pending:     { color: 'var(--text-muted)', label: 'Pending',      bg: 'rgba(255,255,255,0.05)' },
  in_progress: { color: 'var(--info)',       label: 'In Progress',  bg: 'rgba(59,130,246,0.08)' },
  submitted:   { color: 'var(--warning)',    label: 'Submitted',    bg: 'rgba(245,158,11,0.08)' },
  approved:    { color: 'var(--success)',    label: 'Approved',     bg: 'rgba(34,197,94,0.08)' },
  rejected:    { color: 'var(--error)',      label: 'Needs Revision', bg: 'rgba(239,68,68,0.08)' },
};

const getDaysToFinale = (config) => {
  if (!config.finale_date) return null;
  const finale = new Date(config.finale_date);
  const today = new Date();
  const diff = Math.ceil((finale - today) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
};

export default function SponsorshipsPage() {
  const [sponsorships, setSponsorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSponsorship, setSelectedSponsorship] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [expandedImpact, setExpandedImpact] = useState({});
  const [platformConfig, setPlatformConfig] = useState({});
  const [projectData, setProjectData] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [showReceiptPanel, setShowReceiptPanel] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    sponsorshipsAPI.getMy()
      .then(r => setSponsorships(r.data))
      .catch(() => toast.error('Failed to load sponsorships'))
      .finally(() => setLoading(false));
    publicAPI.getConfig().then(r => setPlatformConfig(r.data)).catch(() => {});
  }, []);

  const viewApplicants = async (sp) => {
    setSelectedSponsorship(sp);
    setLoadingApplicants(true);
    try {
      const res = await sponsorshipsAPI.getApplicants(sp.id);
      setApplicants(res.data);
    } catch {
      toast.error('Failed to load applicants');
    } finally {
      setLoadingApplicants(false);
    }
  };

  const selectAwardee = async (studentId, teamId) => {
    const isTeam = !!teamId;
    if (!window.confirm(isTeam ? 'Select this team as awardees? This cannot be undone.' : 'Select this student as your awardee? This cannot be undone.')) return;
    try {
      const payload = isTeam ? { team_id: teamId } : { student_id: studentId };
      await sponsorshipsAPI.selectAwardee(selectedSponsorship.id, payload);
      toast.success(isTeam ? 'Team selected as awardees! They have been notified.' : 'Awardee selected! They have been notified.');
      setSelectedSponsorship(null);
      sponsorshipsAPI.getMy().then(r => setSponsorships(r.data));
    } catch {
      toast.error('Selection failed. Please try again.');
    }
  };

  const reload = () => sponsorshipsAPI.getMy().then(r => setSponsorships(r.data)).catch(() => {});

  const handleDelete = async (sp) => {
    if (!window.confirm('Delete your pledge to "' + sp.topic_title + '"? This cannot be undone.')) return;
    setDeleting(sp.id);
    try {
      await sponsorshipsAPI.delete(sp.id);
      toast.success('Pledge deleted.');
      reload();
    } catch (err) {
      toast.error(err && err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Failed to delete pledge.');
    } finally {
      setDeleting(null);
    }
  };

  const eligiblePledges = sponsorships.filter(s => s.status === 'pledged' && (!s.payment_status || s.payment_status === 'pending'));
  const checkedTotal = sponsorships.filter(s => checkedIds.has(s.id)).reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
  const toggleCheck = (id) => setCheckedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const openReceiptPanel = () => { setCheckedIds(new Set()); setReceiptFile(null); setShowReceiptPanel(true); };
  const handleReceiptUpload = async () => {
    if (checkedIds.size === 0) { toast.error('Select at least one pledge.'); return; }
    if (!receiptFile) { toast.error('Please attach your bank receipt file.'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      formData.append('sponsorship_ids', JSON.stringify([...checkedIds]));
      await sponsorshipsAPI.uploadReceipt(formData);
      toast.success('Receipt uploaded. The admin will verify shortly.');
      setShowReceiptPanel(false);
      reload();
    } catch (err) {
      toast.error(err && err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Failed to upload receipt.');
    } finally {
      setUploading(false);
    }
  };

  const toggleImpact = async (sp) => {
    const isOpen = expandedImpact[sp.id];
    setExpandedImpact(e => ({ ...e, [sp.id]: !isOpen }));
    if (!isOpen && !projectData[sp.id]) {
      try {
        const projects = await projectsAPI.getAll();
        const matched = projects.data.find(p =>
          p.topic_id === sp.topic_id || p.topic_title === sp.topic_title
        );
        if (matched) {
          const full = await projectsAPI.getOne(matched.id);
          setProjectData(d => ({ ...d, [sp.id]: full.data }));
        }
      } catch { }
    }
  };

  if (loading) return <LoadingPage />;

  const totalPledged = sponsorships.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
  const activeCount = sponsorships.filter(s => s.status === 'active').length;

  return (
    <div>
      <PageHeader
        eyebrow="Dashboard"
        title="My Sponsorships"
        subtitle="Track the projects you are funding and select your awardees"
        actions={
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>

            <a href="/honour" target="_blank" rel="noopener noreferrer" className="btn btn-ghost" style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ExternalLink size={15} /> Honour Roll
            </a>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard/marketplace')}>
              <Heart size={16} /> Sponsor a Topic
            </button>
          </div>
        }
      />

      {sponsorships.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 32 }}>
          <div className="card" style={{ padding: '20px 24px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Total Pledged</div>
            <div style={{ fontSize: '1.8rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--gold)' }}>
              ₦{Number(totalPledged).toLocaleString()}
            </div>
          </div>
          <div className="card" style={{ padding: '20px 24px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Topics Sponsored</div>
            <div style={{ fontSize: '1.8rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--teal)' }}>
              {sponsorships.length}
            </div>
          </div>
          <div className="card" style={{ padding: '20px 24px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Active Projects</div>
            <div style={{ fontSize: '1.8rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--success)' }}>
              {activeCount}
            </div>
          </div>
        </div>
      )}

      {sponsorships.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }} onClick={openReceiptPanel}>
            <Upload size={15} /> Upload Receipt
          </button>
        </div>
      )}
      {sponsorships.length === 0 ? (
        <EmptyState
          icon={Heart}
          title="No sponsorships yet"
          description="Browse the marketplace to find research topics worth funding."
          actions={
            <button className="btn btn-primary" onClick={() => navigate('/dashboard/marketplace')}>
              Browse Marketplace
            </button>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {sponsorships.map(sp => {
            const cfg = STATUS_CONFIG[sp.status] || STATUS_CONFIG.pledged;
            const isImpactOpen = expandedImpact[sp.id];
            const proj = projectData[sp.id];

            return (
              <div key={sp.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Sponsorship header */}
                <div style={{ padding: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <span style={{ fontSize: '0.7rem', fontFamily: 'var(--font-mono)', color: cfg.color, textTransform: 'uppercase' }}>
                          ● {cfg.label}
                        </span>
                        {sp.payment_status && sp.receipt_url && (
                          <span style={{
                            fontSize: '0.68rem', padding: '2px 8px', borderRadius: 999,
                            fontFamily: 'var(--font-mono)',
                            background: sp.payment_status === 'verified' ? 'rgba(34,197,94,0.1)' : sp.payment_status === 'rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                            color: sp.payment_status === 'verified' ? 'var(--success)' : sp.payment_status === 'rejected' ? 'var(--error)' : 'var(--warning)',
                            marginLeft: 6,
                          }}>
                            {sp.payment_status === 'verified' ? '✓ Payment verified' : sp.payment_status === 'rejected' ? '⚠ Receipt issue' : '⏳ Awaiting verification'}
                          </span>
                        )}
                        {sp.domain && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--teal)', background: 'rgba(45,212,191,0.1)', padding: '2px 8px', borderRadius: 4 }}>
                            {sp.domain}
                          </span>
                        )}
                      </div>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
                        {sp.topic_title}
                      </h3>
                      <div style={{ display: 'flex', gap: 20, fontSize: '0.8rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                        <span>{sp.lecturer_name}</span>
                        {sp.department && <span>· {sp.department}</span>}
                        <span style={{ color: 'var(--gold)', fontWeight: 600 }}>
                          ₦{Number(parseFloat(sp.amount)).toLocaleString()}
                        </span>
                        {sp.applicant_count > 0 && sp.status !== 'active' && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--teal)' }}>
                            <Users size={13} /> {sp.applicant_count} applicant{sp.applicant_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, marginLeft: 16, flexShrink: 0, alignItems: 'center' }}>
                      {!['active', 'completed'].includes(sp.status) && sp.application_mode !== 'student_own' && sp.poster_role !== 'student' && (
                        <button className="btn btn-primary"
                          style={{ fontSize: '0.8rem', padding: '8px 16px' }}
                          onClick={() => viewApplicants(sp)}>
                          <Users size={14} /> Review Applicants
                        </button>
                      )}
                      {sp.status === 'active' && (
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: '0.8rem', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6 }}
                          onClick={() => toggleImpact(sp)}
                        >
                          {isImpactOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          {isImpactOpen ? 'Hide' : 'View Progress'}
                        </button>
                      )}
                      {sp.status === 'pledged' && (!sp.payment_status || sp.payment_status === 'pending') && (
                        <button
                          className="btn btn-ghost"
                          title="Delete this pledge"
                          style={{ fontSize: '0.8rem', padding: '8px 12px', color: 'var(--error)' }}
                          disabled={deleting === sp.id}
                          onClick={() => handleDelete(sp)}
                        >
                          {deleting === sp.id ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={14} />}
                        </button>
                      )}
                    </div>
                  </div>

                  {sp.selection_criteria && (
                    <div style={{
                      marginTop: 14, padding: '10px 14px',
                      background: 'rgba(255,255,255,0.03)', borderRadius: 6,
                      fontSize: '0.8rem', color: 'var(--text-muted)',
                      borderLeft: '3px solid var(--gold-dim)'
                    }}>
                      <strong style={{ color: 'var(--gold-dim)' }}>Selection criteria:</strong> {sp.selection_criteria}
                    </div>
                  )}
                </div>

                {/* Impact / milestone view for active sponsorships */}
                {sp.status === 'active' && isImpactOpen && (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '20px 24px', background: 'rgba(255,255,255,0.015)' }}>
                    {!proj ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading project data…</div>
                    ) : (
                      <>
                        {/* Student + readiness header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Funded student</div>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{proj.student_name || '—'}</div>
                            {proj.department && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{proj.department}</div>}
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            {(() => { const d = getDaysToFinale(platformConfig); return d ? (<div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 8 }}><span style={{ color: d < 30 ? "var(--warning)" : "var(--teal)", fontWeight: 600 }}>{d}</span> days to finale</div>) : null; })()}
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Finale readiness</div>
                            <div style={{ fontSize: '1.6rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--teal)' }}>
                              {proj.milestones?.length
                                ? Math.round((proj.milestones.filter(m => m.status === 'approved').length / proj.milestones.length) * 100)
                                : 0}%
                            </div>
                          </div>
                        </div>

                        {/* Progress bar */}
                        {proj.milestones?.length > 0 && (() => {
                          const total = proj.milestones.length;
                          const done = proj.milestones.filter(m => m.status === 'approved').length;
                          const pct = Math.round((done / total) * 100);
                          return (
                            <div style={{ marginBottom: 20 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                                <span>Progress</span>
                                <span>{done} / {total} milestones approved</span>
                              </div>
                              <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, var(--teal-dim), var(--teal))', borderRadius: 4, transition: 'width 0.6s ease' }} />
                              </div>
                            </div>
                          );
                        })()}

                        {/* Milestone timeline */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {[...(proj.milestones || [])]
                            .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                            .map((m, i) => {
                              const mCfg = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.pending;
                              return (
                                <div key={m.id} style={{
                                  padding: '10px 14px', borderRadius: 8,
                                  background: mCfg.bg,
                                  border: `1px solid ${m.status === 'approved' ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.05)'}`,
                                  display: 'flex', alignItems: 'flex-start', gap: 12,
                                }}>
                                  <div style={{
                                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                                    background: m.status === 'approved' ? 'var(--success)' : 'rgba(255,255,255,0.08)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.65rem', fontWeight: 600,
                                    color: m.status === 'approved' ? '#fff' : 'var(--text-muted)',
                                  }}>
                                    {m.status === 'approved' ? <CheckCircle size={12} /> : i + 1}
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{m.title}</span>
                                      <span style={{ fontSize: '0.7rem', color: mCfg.color, fontFamily: 'var(--font-mono)' }}>{mCfg.label}</span>
                                    </div>
                                    {m.submission_notes && (
                                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                                        {m.submission_notes}
                                      </div>
                                    )}
                                    {m.submission_url && (
                                      <div style={{ marginTop: 6 }}>
                                        <img
                                          src={m.submission_url}
                                          alt="Milestone photo"
                                          style={{ maxWidth: 160, maxHeight: 100, borderRadius: 6, objectFit: 'cover', cursor: 'pointer' }}
                                          onClick={() => window.open(m.submission_url, '_blank')}
                                          onError={e => { e.target.style.display = 'none'; }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>

                        {/* Latest update callout */}
                        {(() => {
                          const latest = [...(proj.milestones || [])]
                            .filter(m => m.submitted_at)
                            .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0];
                          if (!latest) return null;
                          return (
                            <div style={{
                              marginTop: 16, padding: '12px 14px',
                              background: 'rgba(45,212,191,0.06)', borderRadius: 8,
                              border: '1px solid rgba(45,212,191,0.15)',
                              fontSize: '0.82rem', color: 'var(--text-secondary)',
                            }}>
                              <span style={{ color: 'var(--teal)', fontWeight: 500 }}>Latest update · </span>
                              <span style={{ color: 'var(--text-muted)' }}>
                                {new Date(latest.submitted_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} — </span>
                              {latest.title}: {latest.submission_notes || 'Update submitted'}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Applicants Modal */}
      <Modal
        open={!!selectedSponsorship}
        onClose={() => setSelectedSponsorship(null)}
        title={`Applicants — ${selectedSponsorship?.topic_title}`}
        size="lg"
      >
        <ApplicantsList
          applicants={applicants}
          loading={loadingApplicants}
          onSelect={selectAwardee}
          sponsorship={selectedSponsorship}
        />
      </Modal>

      {/* Bulk Receipt Modal */}
      <Modal open={showReceiptPanel} onClose={() => !uploading && setShowReceiptPanel(false)} title="Upload Payment Receipt" size="md">
        <div style={{ padding: '0 4px' }}>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 20 }}>Tick the pledges covered by your bank transfer, then attach your receipt.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {eligiblePledges.map(sp => (
              <label key={sp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: checkedIds.has(sp.id) ? 'rgba(201,162,39,0.08)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (checkedIds.has(sp.id) ? 'rgba(201,162,39,0.35)' : 'rgba(255,255,255,0.07)'), borderRadius: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={checkedIds.has(sp.id)} onChange={() => toggleCheck(sp.id)} style={{ accentColor: 'var(--gold)', width: 16, height: 16, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sp.topic_title}</div></div>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--gold)', flexShrink: 0 }}>&#8358;{Number(parseFloat(sp.amount)).toLocaleString()}</span>
              </label>
            ))}
          </div>
          {checkedIds.size > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', marginBottom: 16, background: 'rgba(45,212,191,0.06)', borderRadius: 8, fontSize: '0.85rem' }}><span>{checkedIds.size} pledge{checkedIds.size !== 1 ? 's' : ''} selected</span><span style={{ fontWeight: 700, color: 'var(--teal)' }}>Total: &#8358;{Number(checkedTotal).toLocaleString()}</span></div>}
          <div style={{ border: '2px dashed ' + (receiptFile ? 'var(--teal)' : 'rgba(255,255,255,0.15)'), borderRadius: 10, padding: '20px 16px', textAlign: 'center', cursor: 'pointer', marginBottom: 20 }} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
            <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => setReceiptFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
            {receiptFile ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}><FileText size={18} style={{ color: 'var(--teal)' }} /><span style={{ fontSize: '0.85rem', color: 'var(--teal)', fontWeight: 500 }}>{receiptFile.name}</span><button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }} onClick={e => { e.stopPropagation(); setReceiptFile(null); }}><X size={15} /></button></div> : <><Upload size={22} style={{ color: 'var(--text-muted)', marginBottom: 8 }} /><div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Click to attach receipt (PDF, JPG, PNG)</div></>}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setShowReceiptPanel(false)} disabled={uploading}>Cancel</button>
            <button className="btn btn-primary" onClick={handleReceiptUpload} disabled={uploading || checkedIds.size === 0 || !receiptFile}>{uploading ? 'Uploading...' : 'Submit Receipt'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ApplicantsList({ applicants, loading, onSelect, sponsorship }) {
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><LoadingPage /></div>;
  const teams = {};
  const individuals = [];
  for (const app of applicants) {
    if (app.team_id) {
      if (!teams[app.team_id]) teams[app.team_id] = [];
      teams[app.team_id].push(app);
    } else {
      individuals.push(app);
    }
  }
  const teamGroups = Object.values(teams);
  const canSelect = sponsorship && !['active','completed','cancelled'].includes(sponsorship.status);
  return (
    <div>
      {applicants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>No applications received yet.</div>
      ) : (
        <>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 20 }}>
            {applicants.length} applicant{applicants.length !== 1 ? 's' : ''} for this topic.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {teamGroups.map(members => {
              const lead = members.find(m => m.is_lead) || members[0];
              return (
                <div key={lead.team_id} style={{ padding: '16px 18px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(201,162,39,0.2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '0.7rem', background: 'rgba(42,157,143,0.15)', color: 'var(--teal)', padding: '2px 8px', borderRadius: 4, marginBottom: 6, display: 'inline-block' }}>Team of {members.length}</span>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{lead.first_name} {lead.last_name} <span style={{ fontSize: '0.75rem', color: 'var(--gold)', fontWeight: 400 }}>(Team Lead)</span></div>
                      {lead.gpa && <div style={{ fontSize: '0.78rem', color: 'var(--gold)', marginBottom: 4 }}>GPA: {lead.gpa}</div>}
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 8 }}>Members: {members.filter(m => !m.is_lead).map(m => m.first_name + ' ' + m.last_name).join(', ')}</div>
                      {lead.skills && lead.skills.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                          {lead.skills.slice(0,5).map(s => <span key={s} style={{ fontSize: '0.7rem', padding: '2px 7px', background: 'rgba(45,212,191,0.1)', color: 'var(--teal)', borderRadius: 4 }}>{s}</span>)}
                        </div>
                      )}
                      {lead.statement_of_interest && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{lead.statement_of_interest}</div>}
                    </div>
                    <div style={{ marginLeft: 16 }}>
                      {canSelect && <button className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '6px 12px' }} onClick={() => onSelect(null, lead.team_id)}><Award size={13} /> Select Team</button>}
                    </div>
                  </div>
                </div>
              );
            })}
            {individuals.map(app => (
              <div key={app.id} style={{ padding: '16px 18px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--text-primary)' }}>{app.first_name} {app.last_name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {app.matric_number && <span>{app.matric_number}</span>}
                      {app.department && <span>{app.department}</span>}
                      {app.level && <span>{app.level}</span>}
                      {app.gpa && <span style={{ color: 'var(--gold)', display: 'flex', alignItems: 'center', gap: 4 }}><Star size={11} /> GPA: {app.gpa}</span>}
                    </div>
                    {app.skills && app.skills.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                        {app.skills.slice(0,5).map(skill => <span key={skill} style={{ fontSize: '0.7rem', padding: '2px 8px', background: 'rgba(45,212,191,0.1)', color: 'var(--teal)', borderRadius: 4 }}>{skill}</span>)}
                      </div>
                    )}
                    {app.statement_of_interest && <div style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{app.statement_of_interest}</div>}
                  </div>
                  <div style={{ marginLeft: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {app.cv_url && <a href={'/' + app.cv_url} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '6px 12px' }}><BookOpen size={13} /> CV</a>}
                    {canSelect && <button className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '6px 12px' }} onClick={() => onSelect(app.student_id, null)}><Award size={13} /> Select</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
